import Papa from 'papaparse';

interface LandmineData {
  aiuIndex?: number;
  trueDepth?: number;
  notes?: string | number;
}

interface DatasetLayer {
  [itemId: string]: LandmineData;
}

export interface WebMapData {
  [datasetId: string]: DatasetLayer;
}

interface ColumnMapping {
  datasetId: string;
  field: 'aiuIndex' | 'trueDepth' | 'notes';
  columnIndex: number;
}

/**
 * Parses the seeded field datalog CSV into a structured object organized by dataset ID
 * @param csvContent - The raw CSV content as a string
 * @returns WebMapData object organized by dataset ID, then by item ID
 */
export function parseSeededFieldDatalog(csvContent: string): WebMapData {
  // Use papaparse to properly handle CSV with quoted fields
  const parsed = Papa.parse(csvContent, {
    header: false,
    skipEmptyLines: true,
    dynamicTyping: false, // Keep all values as strings initially
  });

  if (!parsed.data || parsed.data.length < 2) {
    throw new Error('CSV must contain at least a header and one data row');
  }

  const rows = parsed.data as string[][];
  const headers = rows[0].map(h => h.trim());
  const result: WebMapData = {};

  // Create column mappings by parsing headers
  const columnMappings: ColumnMapping[] = [];

  headers.forEach((header, index) => {
    if (header === 'Item ID') return; // Skip the Item ID column

    // Parse headers like "Dataset ID 1 : AIU Index" or "Dataset ID 3-1 : True Depth"
    const match = header.match(/^Dataset ID (.+?)\s*:\s*(.+)$/);
    if (match) {
      const datasetId = match[1].trim();
      const fieldName = match[2].trim();

      let field: 'aiuIndex' | 'trueDepth' | 'notes';
      switch (fieldName.toLowerCase()) {
        case 'aiu index':
          field = 'aiuIndex';
          break;
        case 'true depth':
          field = 'trueDepth';
          break;
        case 'notes':
          field = 'notes';
          break;
        default:
          return; // Skip unknown fields
      }

      columnMappings.push({
        datasetId,
        field,
        columnIndex: index
      });
    }
  });

  // Process data rows
  for (let i = 1; i < rows.length; i++) {
    const row = rows[i];
    const itemId = row[0]?.trim();

    if (!itemId) continue; // Skip rows without Item ID

    // Process each column mapping for this row
    columnMappings.forEach(mapping => {
      const cellValue = row[mapping.columnIndex]?.trim();

      // Skip empty cells
      if (!cellValue || cellValue === '') return;

      // Initialize dataset if it doesn't exist
      if (!result[mapping.datasetId]) {
        result[mapping.datasetId] = {};
      }

      // Initialize item if it doesn't exist in this dataset
      if (!result[mapping.datasetId][itemId]) {
        result[mapping.datasetId][itemId] = {};
      }

      // Parse and assign the value based on field type
      if (mapping.field === 'notes') {
        // Notes can be string or number, keep as-is
        const numValue = parseFloat(cellValue);
        result[mapping.datasetId][itemId][mapping.field] = isNaN(numValue) ? cellValue : numValue;
      } else {
        // AIU Index and True Depth should be numbers
        const numValue = parseFloat(cellValue);
        if (!isNaN(numValue)) {
          result[mapping.datasetId][itemId][mapping.field] = numValue;
        }
      }
    });
  }

  return result;
}

/**
 * Helper function to get all dataset IDs from the parsed data
 * @param data - Parsed WebMapData
 * @returns Array of dataset IDs
 */
export function getDatasetIds(data: WebMapData): string[] {
  return Object.keys(data).sort((a, b) => {
    // Custom sort to handle numeric and alphanumeric dataset IDs properly
    const aMatch = a.match(/^(\d+)(?:-(\d+))?$/);
    const bMatch = b.match(/^(\d+)(?:-(\d+))?$/);

    if (aMatch && bMatch) {
      const aMain = parseInt(aMatch[1]);
      const bMain = parseInt(bMatch[1]);
      if (aMain !== bMain) return aMain - bMain;

      const aSub = aMatch[2] ? parseInt(aMatch[2]) : 0;
      const bSub = bMatch[2] ? parseInt(bMatch[2]) : 0;
      return aSub - bSub;
    }

    return a.localeCompare(b);
  });
}

/**
 * Helper function to get all landmine IDs for a specific dataset
 * @param data - Parsed WebMapData
 * @param datasetId - Dataset ID to get landmines for
 * @returns Array of item IDs (landmines) in the dataset
 */
export function getLandmineIds(data: WebMapData, datasetId: string): string[] {
  return data[datasetId] ? Object.keys(data[datasetId]) : [];
}

/**
 * Helper function to get landmine data for a specific dataset and item
 * @param data - Parsed WebMapData
 * @param datasetId - Dataset ID
 * @param itemId - Item ID (landmine)
 * @returns LandmineData or undefined if not found
 */
export function getLandmineData(data: WebMapData, datasetId: string, itemId: string): LandmineData | undefined {
  return data[datasetId]?.[itemId];
}

/**
 * Gets AIU Index color based on value
 * @param aiuIndex - AIU Index value (0-3)
 * @returns Color string for the marker
 */
export function getAIUColor(aiuIndex: number | undefined): string {
  if (aiuIndex === undefined || aiuIndex === null) {
    return '#808080'; // Gray for unknown/no data
  }

  switch (aiuIndex) {
    case 0: return '#FFFFFF'; // White - No anomaly
    case 1: return '#FFFF00'; // Yellow - Weak anomaly  
    case 2: return '#FF8000'; // Orange - Moderate anomaly
    case 3: return '#FF0000'; // Red - Strong anomaly
    default: return '#808080'; // Gray for invalid values
  }
}

// Example usage:
// const csvContent = await fetch('path/to/your/csv').then(r => r.text());
// const parsedData = parseSeededFieldDatalog(csvContent);
// 
// // Get landmine data for specific dataset and item
// const landmineData = getLandmineData(parsedData, datasetId, 'A1');
// 
// // Get color for AIU Index
// const color = getAIUColor(landmineData?.aiuIndex);
